/**
 * Shared constants: enums, statuses, error codes.
 * Keep synchronized with prisma/schema.prisma enums.
 */

export const ROLES = Object.freeze({
  SUPER_ADMIN: "SUPER_ADMIN", // BMG leadership (Toni, Nick) — full platform access
  ADMIN: "ADMIN",   // Legacy BMG staff — treated as SUPER_ADMIN in auth checks
  EMPLOYEE: "EMPLOYEE", // BMG staff scoped to assigned clients (Phase 3b)
  ACCOUNTANT: "ACCOUNTANT", // BMG bookkeeper — billing + read-only contracts (Phase 4c)
  OWNER: "OWNER",   // Client business owner
  SALES: "SALES",   // Client salesperson
  CLIENT: "CLIENT", // Client dashboard user (read-only)
});

/**
 * Full-admin check. SUPER_ADMIN and ADMIN both have unrestricted access —
 * SUPER_ADMIN ⊇ ADMIN. Phase 3a promoted Toni/Nick to SUPER_ADMIN, so every
 * `role === "ADMIN"` gate had to become `isAdmin(role)` to keep working.
 * Single source of truth, imported by both the API and the web app.
 */
export function isAdmin(role) {
  return role === ROLES.SUPER_ADMIN || role === ROLES.ADMIN;
}

/** Billing-desk check: SUPER_ADMIN/ADMIN plus the ACCOUNTANT role (Phase 4c). */
export function isBilling(role) {
  return isAdmin(role) || role === ROLES.ACCOUNTANT;
}

export const DATA_SOURCE_STATUS = Object.freeze({
  CONNECTED: "connected",
  DISCONNECTED: "disconnected",
  ERROR: "error",
  PENDING: "pending",
});

export const SYNC_STATUS = Object.freeze({
  STARTED: "started",
  COMPLETED: "completed",
  FAILED: "failed",
});

export const ERROR_CODES = Object.freeze({
  VALIDATION_ERROR: "VALIDATION_ERROR",
  UNAUTHORIZED: "UNAUTHORIZED",
  FORBIDDEN: "FORBIDDEN",
  NOT_FOUND: "NOT_FOUND",
  CONFLICT: "CONFLICT",
  GONE: "GONE",
  UNPROCESSABLE: "UNPROCESSABLE",
  BAD_GATEWAY: "BAD_GATEWAY",
  INTERNAL_ERROR: "INTERNAL_ERROR",
});

/**
 * Default theme tokens. Only `accent` changes per client; the rest are static.
 * Keep in sync with packages/web/src/lib/theme.js.
 */
export const DEFAULT_ACCENT = "#ff6804"; // BMG Orange

/**
 * Line-item category → display label. INSERTION ORDER IS THE GROUPING ORDER
 * used by the builder's quick-add panel, the proposal PDF, the send email,
 * and the public accept page. Keep it the single source of truth.
 */
export const LINE_ITEM_CATEGORY_LABELS = Object.freeze({
  website: "Website", social: "Social Media", ads: "Paid Advertising", seo: "SEO", email: "Email Marketing",
  content: "Content", hosting: "Hosting", software: "Software", licensing: "Licensing", other: "Other",
});

/**
 * Canonical data-source type → Windsor.ai source id.
 *
 * Single source of truth for this mapping, used by:
 *   - the new-client wizard + admin source forms (frontend)
 *   - the source-test endpoint and the create-source default (backend)
 *   - the windsorSourceId backfill script
 *
 * The sync pipeline (api/services/windsor.js) resolves the Windsor source from
 * a source's `type`, so this map is the authority — a DataSource.windsorSourceId
 * is just a stored override.
 */
export const WINDSOR_SOURCE_BY_TYPE = Object.freeze({
  ga4:                     "google_analytics_4",
  google_search_console:   "google_search_console",
  google_ads:              "google_ads",
  shopify:                 "shopify",
  meta_ads:                "facebook_ads",
  linkedin_ads:            "linkedin_ads",
  youtube_analytics:       "youtube_analytics",
  google_business_profile: "google_business_profile",
});

/**
 * Social data vocabulary (Metricool provider + partner ingestion). These are
 * the ONLY keys the social writer accepts — see api/services/socialMetrics.js.
 */
export const SOCIAL_NETWORKS = Object.freeze([
  "instagram", "facebook", "linkedin", "gmb", "tiktok",
  "youtube", "twitter", "pinterest", "threads", "bluesky",
]);

export const SOCIAL_METRIC_KEYS = Object.freeze([
  "followers", "impressions", "reach", "interactions", "engagement",
  "posts", "calls", "directions", "website_clicks",
]);

/** Partner-API scopes (v1). Checked by api/middleware/partnerAuth.js. */
export const PARTNER_SCOPES = Object.freeze(["clients:read", "social:write"]);

/**
 * Change #4 — how a line's relationship to the catalog was DECIDED.
 *
 * `unlinked` is NOT a kind of custom. It means nobody has decided yet: legacy
 * rows, AI-extracted lines, imports without a trusted mapping. Treating it as
 * "custom" would convert an absence of knowledge into an assertion, which is
 * the exact inference this change exists to prevent.
 *
 * Only `catalog` and `custom` may be chosen by an API caller; `unlinked`
 * classifies rows whose provenance was never established.
 */
export const SERVICE_LINK_STATES = Object.freeze(["catalog", "custom", "unlinked"]);

/**
 * Change #3 — ClientService lifecycle. There is no "paused": a temporary stop
 * is recorded as `ended`, and resuming inserts a NEW period. The history is
 * then honest about the gap instead of hiding it inside a third status.
 *
 * An `ended` row is never reactivated — no code path sets status back to
 * "active". Mirrored by the DB CHECK "ClientService_status_check".
 */
export const CLIENT_SERVICE_STATUSES = Object.freeze(["active", "ended"]);
